/*
    REPORT COMPLIANCE TEXT

    When a session contains injections given under a particular guidance modality (EMG or
    ultrasound), the user's default compliance text for that modality is appended to the session's
    Treating Physician Notes, so the backend picks it up when it renders, prints or emails the report.

    THE DOUBLE-ADD PROBLEM

    The clinician is free to reword the text after it has been added, so "has this already been
    added?" cannot be answered by comparing the notes against the preference text. Two things make
    that worse:

        - the notes are per SESSION (shared by everyone in the practice), while the preferences are
          per USER. Remembering "already added" against the user would double add as soon as a
          second clinician viewed the same session's report.

        - user A's tweaked default text is not user B's, so an exact match against the preference
          fails across users anyway.

    So the marker has to live in the notes themselves, as they are the shared source of truth.
    Each block is written under a fixed heading line, and detection only ever looks for that heading:

        EMG Compliance:
        As clinically indicated, all muscles were injected under EMG guidance ...

    The wording underneath can be rewritten completely and it still will not be added twice.

    NOTE: the headings below are a permanent data format, not a label. Changing one means sessions
          which already carry the old heading stop being recognised, and get a second block appended.
*/

import { USER_PREF } from '@/js/user-prefs-keys';


// matches the maxlength on the Treating Physician Notes textarea in components/pm-report
export const NOTES_MAX_LENGTH = 4000;


/*
    One entry per guidance modality we can detect on a session's injections.

    isPresentInInjection() is given a single injection from /rest/session/{id}/injection/all
    and answers whether that injection used this modality.

    The categories are DISJOINT - each injection belongs to exactly one:

        EMG only, ultrasound only, or both together (the combined star marker on the muscle map).

    So a block is added only when at least one injection of THAT kind exists. A session made up
    solely of combined injections gets the combined block alone, not the EMG and ultrasound ones as
    well - those sentences would describe it as two different kinds of session. A session mixing
    kinds gets one block per kind present.
*/
export const COMPLIANCE_MODALITIES = [
    {
        id                   : 'emg',
        label                : 'EMG',
        heading              : 'EMG Compliance:',
        autoIncludePref      : USER_PREF.REPORT_AUTO_INCLUDE_EMG_COMPLIANCE_TEXT,
        textPref             : USER_PREF.REPORT_EMG_COMPLIANCE_TEXT,
        isPresentInInjection : (inInjection) => inInjection.isEmg == true && inInjection.isUltrasound != true,
    },

    {
        id                   : 'ultrasound',
        label                : 'Ultrasound',
        heading              : 'Ultrasound Compliance:',
        autoIncludePref      : USER_PREF.REPORT_AUTO_INCLUDE_ULTRASOUND_COMPLIANCE_TEXT,
        textPref             : USER_PREF.REPORT_ULTRASOUND_COMPLIANCE_TEXT,
        isPresentInInjection : (inInjection) => inInjection.isUltrasound == true && inInjection.isEmg != true,
    },

    /*
        An injection given under both at once is its own kind - drawn with the combined (star)
        marker on the muscle map - and gets its own compliance text, which also names that marker.
        It deliberately does NOT also count as EMG or as ultrasound above.
    */
    {
        id                   : 'emg-and-ultrasound',
        label                : 'EMG and Ultrasound',
        heading              : 'EMG and Ultrasound Compliance:',
        autoIncludePref      : USER_PREF.REPORT_AUTO_INCLUDE_EMG_AND_ULTRASOUND_COMPLIANCE_TEXT,
        textPref             : USER_PREF.REPORT_EMG_AND_ULTRASOUND_COMPLIANCE_TEXT,
        isPresentInInjection : (inInjection) => inInjection.isEmg == true && inInjection.isUltrasound == true,
    },
];


/*
    Which modalities were used anywhere in this list of injections.
    inInjections is the array from itBnTx.getInjectionsForSession().
*/
export function modalitiesPresentInInjections (inInjections) {
    var injections = inInjections || []

    return COMPLIANCE_MODALITIES.filter(inModality => injections.some(inModality.isPresentInInjection))
}


/*
    Has this modality's compliance block already been added to the notes?

    Deliberately tests the heading ONLY - the compliance wording beneath it may have been edited by
    the clinician, or may have come from another user's differently worded default.

    The heading must occupy a line of its own, which is how appendComplianceBlock always writes it.
    A plain substring test is not enough: "Ultrasound Compliance:" is a substring of
    "EMG and Ultrasound Compliance:", so with indexOf the presence of the combined block would make
    the ultrasound block look already-added and it would never be appended.
*/
export function hasComplianceBlock (inNotes, inHeading) {
    var escapedHeading = inHeading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    // start of text or start of a line, the heading, optional trailing whitespace, end of line or text
    var headingOnItsOwnLine = new RegExp('(^|\\n)' + escapedHeading + '[ \\t]*(\\r?\\n|$)')

    return headingOnItsOwnLine.test(inNotes || '')
}


/*
    Returns the notes with the compliance block appended, separated by a blank line.
    Does not modify inNotes.
*/
export function appendComplianceBlock (inNotes, inHeading, inComplianceText) {
    var existingNotes = (inNotes || '').replace(/\s+$/, '')

    var complianceBlock = inHeading + '\n' + inComplianceText

    return (existingNotes == '') ? complianceBlock
                                 : existingNotes + '\n\n' + complianceBlock
}
